import { TemplateState, TemplateType } from "../types";
import { useCallback } from "react";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "./useTemplateData";

export const useApplySettings = () => {
  const {
    mediaGenerationSettingsData: { mediaGenerationSettings },
    templatesData: { isFlashProject },
    storyboardWithSlidesData: { slides },
  } = useTemplateData();

  const { currentChapter, currentSlide, setTemplates, slideLayoutType } =
    useTemplateEditorStore((store) => ({
      currentChapter: store.currentChapter,
      currentSlide: store.currentSlide,
      setTemplates: store.setTemplates,
      slideLayoutType: store.templateState.slideLayoutType,
    }));
  const { templateId: currentTemplateId } = currentChapter;

  // applyFn defaults to the store's generic setTemplates (whole-field overwrite,
  // used as-is by Logo/Text). Callers whose item mixes identity with style
  // (e.g. Avatar) can pass their own merge function instead.
  const handleApplyToSome = useCallback(
    (
      templateIds: string[],
      partialTemplateState: Partial<TemplateState>,
      applyFn: (
        templateIds: string[],
        partialTemplateState: Partial<TemplateState>
      ) => void = setTemplates
    ) => {
      applyFn(templateIds, partialTemplateState);
    },
    [setTemplates]
  );

  const handleApplyToAll = useCallback(
    (
      partialTemplateState: Partial<TemplateState>,
      applyFn: (
        templateIds: string[],
        partialTemplateState: Partial<TemplateState>
      ) => void = setTemplates,
      allSceneTypes = false
    ) => {
      // Target every chapter/transition scene (custom) or every slide (Flash), whatever its type.
      if (allSceneTypes) {
        const currentId = currentSlide ?? currentTemplateId;
        const sceneIds = isFlashProject
          ? slides.map((slide) => slide.uuid)
          : (mediaGenerationSettings?.chapters.flatMap((chapter) => [
              chapter.chapter_template_id,
              chapter.transition_template_id,
            ]) ?? []);
        applyFn(
          sceneIds.filter((id): id is string => !!id && id !== currentId),
          partialTemplateState
        );
        return;
      }

      if (!isFlashProject) {
        const templateType = currentChapter.type;
        if (templateType === TemplateType.CHAPTER) {
          const templateIdsToProcess =
            mediaGenerationSettings?.chapters.reduce((acc, chapter) => {
              if (!chapter.chapter_template_id) return acc;
              if (chapter.chapter_template_id !== currentTemplateId) {
                acc.push(chapter.chapter_template_id);
              }
              return acc;
            }, [] as string[]) ?? [];
          applyFn(templateIdsToProcess, partialTemplateState);
          return;
        }
        if (templateType === TemplateType.TRANSITION) {
          const templateIdsToProcess =
            mediaGenerationSettings?.chapters.reduce((acc, chapter) => {
              if (!chapter.transition_template_id) return acc;
              if (chapter.transition_template_id !== currentTemplateId) {
                acc.push(chapter.transition_template_id);
              }
              return acc;
            }, [] as string[]) ?? [];
          applyFn(templateIdsToProcess, partialTemplateState);
          return;
        }
      }

      if (slideLayoutType) {
        const templateIdsToProcess = slides.reduce((acc, slide) => {
          if (slide.slide_type !== slideLayoutType) return acc;
          if (slide.uuid && slide.uuid !== currentTemplateId) {
            acc.push(slide.uuid);
          }
          return acc;
        }, [] as string[]);

        applyFn(templateIdsToProcess, partialTemplateState);
        return;
      }
    },
    [
      currentTemplateId,
      currentSlide,
      mediaGenerationSettings?.chapters,
      setTemplates,
      isFlashProject,
      slides,
      slideLayoutType,
      currentChapter.type,
    ]
  );

  return {
    handleApplyToAll,
    handleApplyToSome,
  };
};
